<script setup lang="ts">
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog-v2";
import { ref, watch, nextTick, computed } from "vue";
import { Button } from "@/components/ui/button";
import {
    usePaymentGateway,
    billingDetailsSchema,
    BillingDetails,
} from "../composables/usePaymentGateway";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RequiredStarIcon from "./RequiredStarIcon.svg";

import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";

import { BillingAddress, Plan } from "../types";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import { RadioGroup } from "@/components/ui/radio-group";

import {
    RadioGroupIndicator,
    RadioGroupItem as RadioGroupItemRadix,
} from "radix-vue";

import CheckIconGradient from "./CheckIconGradient.svg";

import { CreditCardIcon, FileText, ShieldCheck } from "lucide-vue-next";
import { formatPriceCurrency } from "../utils/formatPriceCurrency";
import { addMonths, addYears } from "date-fns";
import CountrySelect from "./CountrySelect.vue";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { vAutoAnimate } from "@formkit/auto-animate/vue";
import { ScrollArea } from "@/components/ui/scroll-area";
import BillingInformation from "./BillingInformation.vue";
import CouponInput from "./CouponInput.vue";
import { onMounted } from "vue";
import { useUrlSearchParams } from "@vueuse/core";
import route from "ziggy-js";

const props = defineProps<{
    plans: Plan[];
    clientSecret: string;
    billingAddress: BillingAddress;
    teamId?: number;
}>();

console.log(props.plans);

const { format } = useLocalisedTzFormat();
const params = useUrlSearchParams("history");

const formSchema = toTypedSchema(billingDetailsSchema);

const prepareName = (address: BillingAddress) => {
    if (!address.first_name || !address.last_name) {
        return "";
    }

    return `${address.first_name} ${address.last_name}`;
};

const form = useForm<BillingDetails>({
    validationSchema: formSchema,
    initialValues: {
        name: prepareName(props.billingAddress),
        country: props.billingAddress.country.code,
        postal_code: props.billingAddress.postal_code ?? "",
    },
});

const gateway = usePaymentGateway(props.clientSecret);
const country = ref(props.billingAddress.country.code);
const loading = ref(false);
const opened = ref(false);

const appliedCoupon = ref<string | null>(null);
const couponResult = ref<any>(null);
const finalAmount = ref<number>(0);
const recalculatingCoupon = ref(false);
const showCouponInput = ref(false);

watch(opened, (value) => {
    if (value) {
        nextTick(() => {
            gateway.init();
        });
    } else {
        selectedPlanCycle.value = "option-one";
        selectedPlan.value = yearlyPlan.value;
    }
});

const selectedPlan = ref<Plan>();

// Helper function to find plans by their proper IDs
const findPlanById = (planType: "YEARLY" | "MONTHLY") => {
    // Get user's currency from the first plan (they're all the same currency)
    const currency = props.plans[0]?.currency || "CZK";
    const planId = `ZAPTIME_PRO_${planType}_${currency}`;

    return props.plans.find((plan) => plan.id === planId);
};

// Initialize with yearly plan
const yearlyPlan = computed(() => findPlanById("YEARLY"));
const monthlyPlan = computed(() => findPlanById("MONTHLY"));

// Set initial selection
selectedPlan.value = yearlyPlan.value;

const selectedTab = ref("card");
const selectedPlanCycle = ref("option-one");

watch(selectedTab, (value) => {
    if (value === "card") {
        nextTick(() => {
            gateway.init();
        });
    }
});

watch(
    () => props.plans,
    () => {
        // Re-find the correct plan based on current selection
        if (selectedPlanCycle.value === "option-one") {
            selectedPlan.value = yearlyPlan.value;
        } else {
            selectedPlan.value = monthlyPlan.value;
        }
    },
);

watch(selectedPlanCycle, async (value) => {
    if (value === "option-one") {
        selectedPlan.value = yearlyPlan.value;
    } else {
        selectedPlan.value = monthlyPlan.value;
    }

    // If a coupon is applied, recalculate it for the new plan amount
    if (appliedCoupon.value) {
        await recalculateCoupon();
    } else {
        updateFinalAmount();
    }
});

const updateFinalAmount = () => {
    if (!selectedPlan.value) return;

    if (couponResult.value) {
        finalAmount.value = couponResult.value.pricing.final_amount;
    } else {
        finalAmount.value = selectedPlan.value.price;
    }
};

const recalculateCoupon = async () => {
    if (!appliedCoupon.value || !selectedPlan.value) return;

    recalculatingCoupon.value = true;

    try {
        const response = await fetch(route("app.coupons.validate"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-TOKEN":
                    document
                        .querySelector('meta[name="csrf-token"]')
                        ?.getAttribute("content") || "",
            },
            body: JSON.stringify({
                code: appliedCoupon.value,
                amount: selectedPlan.value.price,
            }),
        });

        const data = await response.json();

        if (response.ok && data.valid) {
            couponResult.value = data;
            updateFinalAmount();
        } else {
            // If coupon is no longer valid for the new amount, remove it
            onCouponRemoved();
        }
    } catch (err) {
        console.error("Failed to recalculate coupon:", err);
        // Keep existing coupon state on network error
    } finally {
        recalculatingCoupon.value = false;
    }
};

const onCouponApplied = (code: string, result: any) => {
    appliedCoupon.value = code;
    couponResult.value = result;
    updateFinalAmount();
};

const onCouponRemoved = () => {
    appliedCoupon.value = null;
    couponResult.value = null;
    updateFinalAmount();
};

const toggleCouponInput = () => {
    showCouponInput.value = !showCouponInput.value;
};

const onSubmit = form.handleSubmit(async (values) => {
    subscribe(values, props.teamId);
});

async function subscribe(billindDetails: BillingDetails, teamId?: number) {
    loading.value = true;
    if (!selectedPlan.value) {
        loading.value = false;
        return;
    }

    const res = await gateway.subscribe(
        selectedPlan.value.plan_id,
        {
            name: billindDetails.name,
            country: billindDetails.country,
            postal_code: billindDetails.postal_code,
        },
        teamId,
        appliedCoupon.value,
    );

    loading.value = false;
    if (!res.ok) {
        console.log(res.stack);
    } else {
        opened.value = false;
    }
}

function checkParamsAndModal() {
    if (!params.preselectedplan) {
        return;
    }

    if (params.preselectedplan === "monthly") {
        selectedPlanCycle.value = "option-two";
    } else {
        selectedPlanCycle.value = "option-one";
    }

    opened.value = true;
}

onMounted(() => {
    checkParamsAndModal();
    updateFinalAmount();
});
</script>

<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent class="sm:max-w-[969px]">
            <DialogHeader>
                <template #icon>
                    <CreditCardIcon
                        class="text-z-primary-600"
                        stroke-width="2.25"
                        :size="20"
                    />
                </template>
                <DialogTitle>{{
                    $t("billing.card.payment_order")
                }}</DialogTitle>
                <DialogDescription> </DialogDescription>
            </DialogHeader>

            <ScrollArea class="max-h-[calc(100vh-100px)]">
                <div
                    class="gap-4 p-4 sm:flex-row-reverse flex w-full flex-col-reverse"
                >
                    <div
                        class="rounded-lg border-iron-100 p-4 sm:max-w-[419px] border"
                    >
                        <h2 class="text-xl font-medium tracking-[-0.02em]">
                            Zaptime Professional
                        </h2>

                        <RadioGroup
                            class="mt-4"
                            v-model:model-value="selectedPlanCycle"
                            default-value="option-one"
                        >
                            <!-- YEARLY OPTION -->
                            <div
                                :key="`yearly-${yearlyPlan?.id}`"
                                class="rounded-lg p-[2px]"
                                :class="[
                                    selectedPlanCycle === 'option-one'
                                        ? 'zap-gradient shadow-md'
                                        : 'ring-iron-200 ring-[1px]',
                                ]"
                            >
                                <div class="bg-white rounded-[calc(8px-2px)]">
                                    <div
                                        class="rounded-xl flex items-center justify-between"
                                    >
                                        <Label
                                            class="gap-4 px-4 py-3 flex w-full items-center"
                                            for="option-one"
                                        >
                                            <RadioGroupItemRadix
                                                id="option-one"
                                                value="option-one"
                                            >
                                                <RadioGroupIndicator>
                                                    <CheckIconGradient></CheckIconGradient>
                                                </RadioGroupIndicator>

                                                <svg
                                                    v-if="
                                                        selectedPlanCycle ===
                                                        'option-two'
                                                    "
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <g
                                                        clip-path="url(#clip0_2636_1564)"
                                                    >
                                                        <path
                                                            d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                                                            stroke="#D1D1D6"
                                                            stroke-width="2"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                    </g>
                                                    <defs>
                                                        <clipPath
                                                            id="clip0_2636_1564"
                                                        >
                                                            <rect
                                                                width="24"
                                                                height="24"
                                                                rx="12"
                                                                fill="white"
                                                            />
                                                        </clipPath>
                                                    </defs>
                                                </svg>
                                            </RadioGroupItemRadix>

                                            <div
                                                class="gap-1.5 flex w-full items-center justify-between"
                                            >
                                                <div>
                                                    <p class="font-medium">
                                                        {{
                                                            $t(
                                                                "billing.card.yearly_payment",
                                                            )
                                                        }}
                                                    </p>

                                                    <p
                                                        class="font-normal text-iron-500 tracking-[-0.01em]"
                                                    >
                                                        {{
                                                            yearlyPlan
                                                                ? formatPriceCurrency(
                                                                      yearlyPlan.price,
                                                                      yearlyPlan.currency,
                                                                  )
                                                                : ""
                                                        }}
                                                    </p>
                                                </div>

                                                <div
                                                    class="zap-gradient rounded-md py-1 text-sm font-medium text-white px-[9px]"
                                                >
                                                    {{
                                                        $t(
                                                            "billing.coupon.you_save",
                                                        )
                                                    }}
                                                </div>
                                            </div>
                                        </Label>
                                    </div>
                                </div>
                            </div>

                            <!-- MONTHLY OPTION -->
                            <div
                                :key="`monthly-${monthlyPlan?.id}`"
                                class="mt-2 rounded-lg p-[2px]"
                                :class="[
                                    selectedPlanCycle === 'option-two'
                                        ? 'zap-gradient shadow-md'
                                        : 'ring-iron-200 ring-[1px]',
                                ]"
                            >
                                <div class="bg-white rounded-[calc(8px-2px)]">
                                    <Label
                                        class="gap-4 px-4 py-3 flex w-full items-center"
                                        for="option-two"
                                    >
                                        <RadioGroupItemRadix
                                            id="option-two"
                                            value="option-two"
                                        >
                                            <RadioGroupIndicator>
                                                <CheckIconGradient></CheckIconGradient>
                                            </RadioGroupIndicator>

                                            <svg
                                                v-if="
                                                    selectedPlanCycle ===
                                                    'option-one'
                                                "
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g
                                                    clip-path="url(#clip0_2636_1564)"
                                                >
                                                    <path
                                                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                                                        stroke="#D1D1D6"
                                                        stroke-width="2"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    />
                                                </g>
                                                <defs>
                                                    <clipPath
                                                        id="clip0_2636_1564"
                                                    >
                                                        <rect
                                                            width="24"
                                                            height="24"
                                                            rx="12"
                                                            fill="white"
                                                        />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                        </RadioGroupItemRadix>

                                        <div>
                                            <p class="font-medium">
                                                {{
                                                    $t(
                                                        "billing.card.monthly_payment",
                                                    )
                                                }}
                                            </p>

                                            <p
                                                class="font-normal text-iron-500 tracking-[-0.01em]"
                                            >
                                                {{
                                                    monthlyPlan
                                                        ? formatPriceCurrency(
                                                              monthlyPlan.price,
                                                              monthlyPlan.currency,
                                                          )
                                                        : ""
                                                }}
                                            </p>
                                        </div>
                                    </Label>
                                </div>
                            </div>
                        </RadioGroup>
                        <div class="mt-4 rounded-lg">
                            <!-- <p class="mt-1 text-sm text-iron-600">
              {{ $t("billing.card.auto_renewal_date") }}
              {{
                selectedPlan.recurring === "monthly"
                  ? format(addMonths(new Date(), 1), "PPP")
                  : format(addYears(new Date(), 1), "PPP")
              }}
            </p> -->
                        </div>

                        <hr class="mt-8" />

                        <p class="mt-4 text-lg font-medium">
                            {{ $t("billing.card.now_you_pay") }}
                        </p>

                        <!-- Coupon Discount Breakdown -->
                        <div v-if="couponResult" class="mt-2 space-y-2">
                            <div
                                class="text-sm flex justify-between"
                                :class="{ 'opacity-50': recalculatingCoupon }"
                            >
                                <span>{{
                                    $t("billing.coupon.original_price")
                                }}</span>
                                <span>{{
                                    selectedPlan
                                        ? formatPriceCurrency(
                                              selectedPlan.price,
                                              selectedPlan.currency,
                                          )
                                        : ""
                                }}</span>
                            </div>
                            <div
                                class="text-sm text-green-600 flex justify-between"
                                :class="{ 'opacity-50': recalculatingCoupon }"
                            >
                                <span
                                    >{{
                                        $t("billing.coupon.discount_amount")
                                    }}
                                    ({{
                                        couponResult.coupon.formatted_value
                                    }})</span
                                >
                                <span
                                    >-{{
                                        formatPriceCurrency(
                                            couponResult.pricing
                                                .discount_amount,
                                            couponResult.pricing.currency,
                                        )
                                    }}</span
                                >
                            </div>
                            <div
                                v-if="recalculatingCoupon"
                                class="text-xs text-iron-500 italic"
                            >
                                {{ $t("billing.coupon.recalculating") }}
                            </div>
                        </div>

                        <div class="mt-2 flex justify-between">
                            <p class="text-lg font-medium tracking-[-0.02em]">
                                {{ $t("billing.card.total") }}
                            </p>
                            <p
                                class="text-2xl font-medium tracking-[-0.02em]"
                                :class="{ 'text-green-600': couponResult }"
                            >
                                {{
                                    selectedPlan
                                        ? formatPriceCurrency(
                                              finalAmount,
                                              selectedPlan.currency,
                                          )
                                        : ""
                                }}
                            </p>
                        </div>

                        <p class="mt-4 text-sm text-iron-500">
                            {{ $t("billing.card.next_payment_amount") }}
                            {{
                                selectedPlan
                                    ? formatPriceCurrency(
                                          selectedPlan.price,
                                          selectedPlan.currency,
                                      )
                                    : ""
                            }}
                            {{ $t("billing.card.will_take_place") }}
                            {{
                                selectedPlan &&
                                selectedPlan.recurring === "monthly"
                                    ? format(addMonths(new Date(), 1), "PPP")
                                    : format(addYears(new Date(), 1), "PPP")
                            }}
                            {{ $t("billing.card.subscription_continue") }}
                        </p>

                        <div
                            class="mt-6 sm:mt-4 flex justify-end tracking-[-0.01em]"
                        >
                            <Button
                                type="submit"
                                form="payment-form"
                                :loading
                                class="w-full"
                                size="lg"
                            >
                                {{ $t("billing.card.pay") }}
                            </Button>
                        </div>

                        <div
                            class="mt-5 gap-1 text-xs font-medium flex items-center justify-center tracking-[-0.01em] text-[#16B364]"
                        >
                            <ShieldCheck :size="20" />
                            {{ $t("billing.card.secure_payment") }}
                        </div>
                    </div>

                    <div
                        class="rounded-lg border-iron-200 bg-iron-50 px-4 py-2 sm:mt-4 sm:w-1/2 sm:px-4 sm:py-5 border"
                    >
                        <form
                            id="payment-form"
                            class="sm:mt-4"
                            @submit.prevent="onSubmit"
                        >
                            <div class="form-row mt-2">
                                <FormField
                                    v-slot="{ componentField }"
                                    name="name"
                                >
                                    <FormItem v-auto-animate>
                                        <FormLabel
                                            class="gap-1 flex items-center"
                                            >{{ $t("billing.card.full_name") }}

                                            <RequiredStarIcon />
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                :placeholder="
                                                    $t('billing.card.full_name')
                                                "
                                                v-bind="componentField"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                </FormField>

                                <div class="mt-4 gap-3 grid grid-cols-2">
                                    <div>
                                        <CountrySelect
                                            required
                                            v-model="country"
                                        />
                                    </div>
                                    <div class="gap-1.5 grid items-center">
                                        <FormField
                                            v-slot="{ componentField }"
                                            name="postal_code"
                                        >
                                            <FormItem v-auto-animate>
                                                <FormLabel
                                                    class="gap-1 flex items-center"
                                                    >{{
                                                        $t(
                                                            "billing.billing_information.zip",
                                                        )
                                                    }}

                                                    <RequiredStarIcon />
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="text"
                                                        :placeholder="
                                                            $t(
                                                                'billing.billing_information.zip',
                                                            )
                                                        "
                                                        v-bind="componentField"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        </FormField>
                                    </div>
                                </div>

                                <!-- Coupon Section -->
                                <div class="mt-4">
                                    <!-- Toggle Question -->
                                    <button
                                        type="button"
                                        v-show="!showCouponInput"
                                        @click="toggleCouponInput"
                                        class="text-sm font-medium text-z-primary-600 hover:text-z-primary-700 cursor-pointer underline"
                                    >
                                        {{ $t("billing.coupon.have_code") }}
                                    </button>

                                    <!-- Coupon Input (conditionally shown) -->
                                    <div v-if="showCouponInput" class="mt-2">
                                        <CouponInput
                                            :amount="selectedPlan?.price || 0"
                                            :currency="
                                                selectedPlan?.currency || 'CZK'
                                            "
                                            :external-result="couponResult"
                                            @applied="onCouponApplied"
                                            @removed="onCouponRemoved"
                                        />
                                    </div>
                                </div>

                                <div class="mt-4 sm:mt-8 w-full">
                                    <Label
                                        class="mb-2 gap-1 flex items-center"
                                        for="postal_code"
                                        >{{ $t("billing.card.card_details") }}

                                        <RequiredStarIcon />
                                    </Label>

                                    <div
                                        id="card-number"
                                        class="rounded-lg border-iron-300 bg-white p-3 h-[43px] w-full border"
                                    >
                                        <!-- A Stripe Element will be inserted here. -->
                                    </div>

                                    <div class="mt-3 gap-x-6 flex">
                                        <div>
                                            <Label
                                                class="gap-1 flex items-center"
                                                for="card-expiry"
                                            >
                                                {{ $t("billing.card.expiry") }}

                                                <RequiredStarIcon />
                                            </Label>
                                            <div
                                                id="card-expiry"
                                                class="mt-1 rounded-lg border-iron-300 bg-white p-3 focus-visible:ring-z-primary-100 col-span-1 w-[124px] border focus-visible:ring-2"
                                            >
                                                <!-- A Stripe Element will be inserted here. -->
                                            </div>
                                        </div>

                                        <div>
                                            <Label
                                                class="gap-1 flex items-center"
                                                for="card-expiry"
                                            >
                                                CVC

                                                <RequiredStarIcon />
                                            </Label>
                                            <div
                                                id="card-cvc"
                                                class="mt-1 rounded-lg border-iron-300 bg-white p-3 col-span-1 w-[108px] border"
                                            >
                                                <!-- A Stripe Element will be inserted here. -->
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Used to display form errors. -->
                                <div id="card-errors" role="alert"></div>
                            </div>
                        </form>

                        <div class="mb-2 mt-5">
                            <hr class="-mx-4 mb-4" />
                            <BillingInformation
                                :billing-address="billingAddress"
                            >
                                <Button
                                    variant="outline"
                                    class="gap-1 text-iron-600 sm:w-auto w-full"
                                >
                                    <FileText :size="20" />

                                    {{
                                        $t("billing.card.enter_billing_details")
                                    }}</Button
                                >
                            </BillingInformation>
                        </div>
                    </div>
                </div>
            </ScrollArea>
        </DialogContent>
    </Dialog>
</template>

<style scoped>
.focused {
    border: 1px solid blue;
    outline: 2px solid black;
    outline-offset: 2px;
}

.zap-gradient {
    background-image: linear-gradient(
        137.17deg,
        #ff8d47 10.41%,
        #ff7447 22.14%,
        #ff4247 46.48%,
        #e92a5b 61%,
        #dd1c67 69.21%,
        #d31071 77.36%,
        #cf0283 89.83%
    );
}
</style>
